import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, Check } from 'lucide-react';
import { OnboardingStep } from './OnboardingStepper';

interface PrototypeSettingsScreenProps {
  isOpen: boolean;
  onClose: () => void;
  currentStep: OnboardingStep;
  onStepChange: (step: OnboardingStep) => void;
  coinsBalance?: number;
  onCoinsBalanceChange?: (balance: number) => void;
}

interface StepOption {
  step: OnboardingStep;
  label: string;
  description: string;
}

const stepOptions: StepOption[] = [
  {
    step: 0,
    label: 'Reverificação necessária',
    description:
      'Usuário já completou o onboarding mas precisa fazer nova verificação facial.',
  },
  {
    step: 1,
    label: 'Etapa 1 — Verificação pendente',
    description: 'Usuário precisa fazer verificação facial.',
  },
  {
    step: 2,
    label: 'Etapa 2 — Precisa comprar moedas',
    description: 'Verificação feita, mas precisa de moedas.',
  },
  {
    step: 3,
    label: 'Etapa 3 — Completar perfil',
    description: 'Tem moedas, mas perfil está incompleto.',
  },
  {
    step: 4,
    label: 'Etapa 4 — Tudo concluído (uso normal)',
    description: 'Todas as etapas concluídas.',
  },
];

export function PrototypeSettingsScreen({
  isOpen,
  onClose,
  currentStep,
  onStepChange,
  coinsBalance,
  onCoinsBalanceChange,
}: PrototypeSettingsScreenProps) {
  const handleSelectStep = (step: OnboardingStep) => {
    onStepChange(step);
    // Close after a short delay to show selection
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex flex-col"
          style={{
            backgroundColor: 'var(--neutral-50)',
          }}
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {/* Header */}
          <div
            className="flex items-center px-4 py-3"
            style={{
              backgroundColor: 'var(--background)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <button
              onClick={onClose}
              className="flex items-center justify-center transition-colors"
              style={{
                width: '40px',
                height: '40px',
                marginRight: '8px',
                color: 'var(--kojo-blue)',
              }}
              aria-label="Voltar"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div className="flex-1">
              <h2
                style={{
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--foreground)',
                  lineHeight: '1.4',
                }}
              >
                Configurações do protótipo
              </h2>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-5 py-5">
              {/* Description */}
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-normal)',
                  color: 'var(--muted-foreground)',
                  lineHeight: '1.5',
                  marginBottom: '20px',
                }}
              >
                Escolha em qual etapa o protótipo deve iniciar.
              </p>

              {/* Options List */}
              <div
                className="overflow-hidden"
                style={{
                  backgroundColor: 'var(--background)',
                  borderRadius: 'var(--radius-card)',
                  border: '1px solid var(--border)',
                }}
              >
                {stepOptions.map((option, index) => {
                  const isSelected = option.step === currentStep;
                  const isLast = index === stepOptions.length - 1;

                  return (
                    <motion.button
                      key={option.step}
                      onClick={() => handleSelectStep(option.step)}
                      className="w-full text-left px-4 py-4 transition-colors"
                      style={{
                        backgroundColor: 'var(--background)',
                        borderBottom: isLast
                          ? 'none'
                          : '1px solid var(--border)',
                      }}
                      whileHover={{ backgroundColor: 'var(--neutral-50)' }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="flex items-start gap-3">
                        {/* Radio indicator */}
                        <div
                          className="flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            border: isSelected
                              ? '6px solid var(--kojo-blue)'
                              : '2px solid var(--border)',
                            backgroundColor: 'var(--background)',
                            transition: 'all 0.2s',
                          }}
                        />

                        {/* Content */}
                        <div className="flex-1 min-w-0 pr-2">
                          <div
                            style={{
                              fontSize: 'var(--text-base)',
                              fontWeight: isSelected
                                ? 'var(--font-weight-semibold)'
                                : 'var(--font-weight-medium)',
                              color: 'var(--foreground)',
                              lineHeight: '1.5',
                              marginBottom: '4px',
                            }}
                          >
                            {option.label}
                          </div>
                          <div
                            style={{
                              fontSize: 'var(--text-sm)',
                              fontWeight: 'var(--font-weight-normal)',
                              color: 'var(--muted-foreground)',
                              lineHeight: '1.5',
                            }}
                          >
                            {option.description}
                          </div>
                        </div>

                        {/* Check icon */}
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{
                              type: 'spring',
                              stiffness: 500,
                              damping: 25,
                            }}
                            className="flex-shrink-0 mt-1"
                          >
                            <Check
                              className="w-5 h-5"
                              style={{ color: 'var(--kojo-blue)' }}
                            />
                          </motion.div>
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Coins Balance Control */}
              {coinsBalance !== undefined && onCoinsBalanceChange && (
                <div className="mt-5">
                  <h3
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--foreground)',
                      marginBottom: '12px',
                    }}
                  >
                    Saldo de moedas do usuário
                  </h3>

                  <div
                    className="overflow-hidden"
                    style={{
                      backgroundColor: 'var(--background)',
                      borderRadius: 'var(--radius-card)',
                      border: '1px solid var(--border)',
                      padding: '16px',
                    }}
                  >
                    {/* Current Balance Display */}
                    <div className="flex items-center justify-between mb-4">
                      <span
                        style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--foreground)',
                        }}
                      >
                        Saldo atual
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--text-lg)',
                          fontWeight: 'var(--font-weight-bold)',
                          color: 'var(--kojo-blue)',
                        }}
                      >
                        {coinsBalance} moedas
                      </span>
                    </div>

                    {/* Quick Preset Buttons */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {[10, 20, 100, 150, 200, 500].map((amount) => (
                        <motion.button
                          key={amount}
                          onClick={() => onCoinsBalanceChange(amount)}
                          className="text-center transition-all"
                          style={{
                            padding: '10px 8px',
                            borderRadius: 'var(--radius-button)',
                            border:
                              coinsBalance === amount
                                ? '2px solid var(--kojo-blue)'
                                : '1px solid var(--border)',
                            backgroundColor:
                              coinsBalance === amount
                                ? 'rgba(59, 130, 246, 0.1)'
                                : 'var(--background)',
                            fontSize: 'var(--text-sm)',
                            fontWeight:
                              coinsBalance === amount
                                ? 'var(--font-weight-semibold)'
                                : 'var(--font-weight-medium)',
                            color:
                              coinsBalance === amount
                                ? 'var(--kojo-blue)'
                                : 'var(--foreground)',
                          }}
                          whileTap={{ scale: 0.96 }}
                        >
                          {amount}
                        </motion.button>
                      ))}
                    </div>

                    {/* Custom Input */}
                    <div>
                      <label
                        style={{
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--muted-foreground)',
                          marginBottom: '6px',
                          display: 'block',
                        }}
                      >
                        Ou defina um valor personalizado
                      </label>
                      <input
                        type="number"
                        value={coinsBalance}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          onCoinsBalanceChange(Math.max(0, value));
                        }}
                        min="0"
                        step="10"
                        className="w-full transition-all"
                        style={{
                          padding: '10px 12px',
                          borderRadius: 'var(--radius-button)',
                          border: '1px solid var(--border)',
                          backgroundColor: 'var(--background)',
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--foreground)',
                          outline: 'none',
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--kojo-blue)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--border)';
                        }}
                      />
                    </div>

                    {/* Helper Text */}
                    <p
                      style={{
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-normal)',
                        color: 'var(--muted-foreground)',
                        lineHeight: '1.5',
                        marginTop: '12px',
                      }}
                    >
                      Para testar os cenários de CTA: use 500 (A, B), 200 (D),
                      20 (C) ou 10 (E)
                    </p>
                  </div>
                </div>
              )}

              {/* Info footer */}
              <div
                className="mt-4 p-3"
                style={{
                  backgroundColor: 'var(--background)',
                  borderRadius: 'var(--radius-card)',
                  border: '1px solid var(--border)',
                }}
              >
                <p
                  style={{
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-weight-normal)',
                    color: 'var(--muted-foreground)',
                    lineHeight: '1.5',
                    textAlign: 'center',
                  }}
                >
                  Este controle é apenas para testes. As mudanças não são
                  persistidas.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
